// Mukti Hospital ERP - Sync Status Indicator (Phase 2c)
// NEW FILE: src/components/Pathology/SyncStatusIndicator.jsx
//
// A small badge that:
//   - starts the auto-sync engine on mount (online event + 30s poll)
//   - shows how many offline bills are still waiting to sync
//   - lets the user trigger a manual sync by clicking
//
// It is self-contained: drop one <SyncStatusIndicator apiKey={...} />
// into the header and everything works. No other wiring needed.

import { useEffect, useState, useCallback } from "react";
import {
  startAutoSync,
  syncPendingBills,
  onSyncStatusChange,
} from "../../services/pathologySyncEngine";
import { getPendingCount } from "../../services/pathologyOutbox";

const SyncStatusIndicator = ({ apiKey }) => {
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const refreshCount = useCallback(async () => {
    const count = await getPendingCount();
    setPending(count);
  }, []);

  useEffect(() => {
    const stopAutoSync = startAutoSync({ apiKey });
    const unsub = onSyncStatusChange((status) => {
      if (typeof status.pending === "number") setPending(status.pending);
      if (typeof status.syncing === "boolean") setSyncing(status.syncing);
    });

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    refreshCount();
    const countTimer = setInterval(refreshCount, 5000);

    return () => {
      stopAutoSync();
      unsub();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(countTimer);
    };
  }, [apiKey, refreshCount]);

  // Nothing pending and online -> show a subtle "all synced" only briefly?
  if (isOnline && pending === 0 && !syncing) {
    return null;
  }

  const handleClick = () => {
    if (!syncing && pending > 0) {
      syncPendingBills({ apiKey });
    }
  };

  let label;
  let color;
  if (syncing) {
    label = `Syncing… (${pending} pending)`;
    color = "#2563eb"; // blue
  } else if (!isOnline) {
    label = pending > 0 ? `Offline — ${pending} bills pending` : "Offline";
    color = pending > 0 ? "#dc2626" : "#6b7280";
  } else {
    label = `${pending} bill${pending > 1 ? 's' : ''} pending — click to sync`;
    color = "#d97706"; // amber
  }

  return (
    <button
      onClick={handleClick}
      title="Pathology offline bills sync status"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "9999px",
        border: `1px solid ${color}`,
        color: color,
        background: "transparent",
        fontSize: "12px",
        fontWeight: 600,
        cursor: pending > 0 && !syncing ? "pointer" : "default",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "9999px",
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </button>
  );
};

export default SyncStatusIndicator;
